<script setup lang="ts">
  import { ref, watch, nextTick, computed, type HTMLAttributes } from 'vue'
  import { IconCheck, IconChevronDown } from '@gat-ship/icons'
  import FlagIcon from '../../flag-icon/FlagIcon.vue'
  import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList
  } from '../command'
  import { Popover, PopoverContent, PopoverTrigger } from '../popover'
  import { ScrollArea } from '../scroll-area'
  import PhoneInput from '../phone-input/PhoneInput.vue'
  import { phoneCodeMapping } from '../phone-input/all-countries'
  import InputGroupAddon from './InputGroupAddon.vue'

  interface Country {
    name: string
    code: string
    dialCode: string
  }

  const countries: Country[] = phoneCodeMapping.map((entry) => ({
    name: String(entry[0]),
    code: String(entry[1]).toUpperCase(),
    dialCode: String(entry[2])
  }))

  /**
   * A phone field that composes the borderless `PhoneInput` with country
   * chrome — the detected / selected country's flag renders as a leading
   * `InputGroupAddon` (not inside the input) whose button opens a combobox
   * dropdown for explicit country selection.
   *
   * It brings no border of its own — drop it into a `FormField` default slot
   * (like `InputGroupInput` / `CountryPicker`) and it picks up the parent
   * `InputGroup`'s focus / error chrome. Behaves exactly like the old
   * single-component `PhoneInput`, just split into primitives.
   *
   * Usage:
   * ```vue
   * <FormField :form="form" name="phone" label="Phone" :validators="v">
   *   <template #default="{ field, invalid }">
   *     <InputGroupPhone
   *       :id="field.name"
   *       :model-value="field.state.value ?? undefined"
   *       :aria-invalid="invalid"
   *       @update:model-value="(val) => field.handleChange(val ?? '')"
   *       @blur="field.handleBlur"
   *     />
   *   </template>
   * </FormField>
   * ```
   */
  const props = defineProps<{
    modelValue?: string | null
    /** External country selection — prepends / swaps the dial code. */
    countryCode?: string
    placeholder?: string
    disabled?: boolean
    id?: string
    dataTestid?: string
    autofocus?: boolean
    ariaInvalid?: boolean
    /** Class forwarded to the inner phone input. */
    inputClass?: HTMLAttributes['class']
  }>()

  const emit = defineEmits<{
    (e: 'update:modelValue', value: string | undefined): void
    (e: 'update:countryCode', value: string | undefined): void
    (e: 'blur', event: FocusEvent): void
  }>()

  const countryDropdownOpen = ref(false)
  const phoneRef = ref<InstanceType<typeof PhoneInput> | null>(null)

  // The flag shown in the leading addon: detection flows UP from `PhoneInput`
  // (`update:countryCode`), independent of the controlled `countryCode` prop
  // we pass DOWN — keeping the two separate avoids a feedback loop.
  const detectedCountryCode = ref<string | undefined>(props.countryCode)
  watch(
    () => props.countryCode,
    (code) => {
      if (code) detectedCountryCode.value = code
    }
  )

  const detectedCountry = computed(() =>
    countries.find((c) => c.code === detectedCountryCode.value?.toUpperCase())
  )

  function onDetectedCountry(code: string | undefined) {
    detectedCountryCode.value = code
    emit('update:countryCode', code)
  }

  function selectCountryFromDropdown(country: Country) {
    countryDropdownOpen.value = false
    phoneRef.value?.selectCountry(country.code)
    nextTick(() => phoneRef.value?.focus())
  }
</script>

<template>
  <div class="flex w-full items-center">
    <!-- Country indicator + dropdown trigger (Popover + Command) -->
    <InputGroupAddon align="inline-start" class="shrink-0 has-[>button]:ml-0">
      <Popover v-model:open="countryDropdownOpen">
        <PopoverTrigger as-child>
          <button
            type="button"
            :disabled="disabled"
            class="flex shrink-0 items-center gap-1"
          >
            <FlagIcon
              v-if="detectedCountry"
              :flag="detectedCountry.code"
              class="size-4 shrink-0 rounded-full"
            />
            <span v-else class="text-muted-foreground/60 text-xs grayscale"
              >🌐</span
            >
            <IconChevronDown class="text-muted-foreground size-3 opacity-50" />
          </button>
        </PopoverTrigger>
        <PopoverContent class="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search country…" />
            <CommandList>
              <CommandEmpty>No country found</CommandEmpty>
              <ScrollArea class="h-[200px]">
                <CommandGroup>
                  <CommandItem
                    v-for="country in countries"
                    :key="country.code"
                    :value="`${country.name} ${country.code} +${country.dialCode} ${country.dialCode}`"
                    class="gap-2"
                    @select="selectCountryFromDropdown(country)"
                  >
                    <FlagIcon
                      :flag="country.code"
                      variant="rectangle"
                      class="bg-foreground/20 flex h-4 w-6 shrink-0 overflow-hidden rounded-sm"
                    />
                    <span class="flex-1 truncate text-sm">
                      {{ country.name }}
                    </span>
                    <span class="text-muted-foreground text-xs tabular-nums">
                      +{{ country.dialCode }}
                    </span>
                    <IconCheck
                      :class="[
                        'ml-auto size-3.5 shrink-0',
                        detectedCountry?.code === country.code
                          ? 'opacity-100'
                          : 'opacity-0'
                      ]"
                    />
                  </CommandItem>
                </CommandGroup>
              </ScrollArea>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </InputGroupAddon>

    <!-- The pure phone input — user types the full number here -->
    <PhoneInput
      :id="id"
      ref="phoneRef"
      :model-value="modelValue"
      :country-code="countryCode"
      :placeholder="placeholder"
      :disabled="disabled"
      :autofocus="autofocus"
      :aria-invalid="ariaInvalid"
      :data-testid="dataTestid"
      :class="inputClass"
      @update:model-value="$emit('update:modelValue', $event)"
      @update:country-code="onDetectedCountry"
      @blur="$emit('blur', $event)"
    />
  </div>
</template>
